import type {
  PaypercutElementsAppearance,
  PaypercutElementsAppearanceRuleProperty,
  PaypercutElementsAppearanceRuleSelector,
  PaypercutElementsAppearanceVariableName,
} from '../types/elements';
import { containsAsciiControlCharacter } from './protocol-validation';

export const ELEMENTS_APPEARANCE_VARIABLE_NAMES = [
  'colorPrimary',
  'colorPrimaryText',
  'colorBackground',
  'colorSurface',
  'colorText',
  'colorTextSecondary',
  'colorTextPlaceholder',
  'colorDanger',
  'colorSuccess',
  'colorWarning',
  'colorBorder',
  'colorFocus',
  'colorMuted',
  'fontFamily',
  'fontSizeBase',
  'fontWeightNormal',
  'fontWeightMedium',
  'lineHeight',
  'spacingUnit',
  'gridColumnSpacing',
  'gridRowSpacing',
  'labelSpacing',
  'borderRadius',
  'buttonBorderRadius',
  'buttonExpressCheckoutBorderRadius',
  'buttonPadding',
] as const satisfies readonly PaypercutElementsAppearanceVariableName[];

export const ELEMENTS_APPEARANCE_RULE_SELECTORS = [
  '.Input',
  '.Input--invalid',
  '.Input:focus',
  '.Label',
  '.Error',
  '.Text',
  '.WalletButton',
  '.WalletButton--applePay',
  '.WalletButton--googlePay',
  '.ActionShell',
] as const satisfies readonly PaypercutElementsAppearanceRuleSelector[];

export const ELEMENTS_APPEARANCE_RULE_PROPERTIES = [
  'backgroundColor',
  'borderColor',
  'borderRadius',
  'borderStyle',
  'borderWidth',
  'borderTopColor',
  'borderTopLeftRadius',
  'borderTopRightRadius',
  'borderTopStyle',
  'borderTopWidth',
  'borderRightColor',
  'borderRightStyle',
  'borderRightWidth',
  'borderBottomColor',
  'borderBottomLeftRadius',
  'borderBottomRightRadius',
  'borderBottomStyle',
  'borderBottomWidth',
  'borderLeftColor',
  'borderLeftStyle',
  'borderLeftWidth',
  'boxShadow',
  'color',
  'fontFamily',
  'fontSize',
  'fontWeight',
  'letterSpacing',
  'lineHeight',
  'opacity',
  'outline',
  'outlineOffset',
  'padding',
  'paddingTop',
  'paddingRight',
  'paddingBottom',
  'paddingLeft',
  'textDecoration',
  'textShadow',
  'textTransform',
  'transition',
] as const satisfies readonly PaypercutElementsAppearanceRuleProperty[];

const VARIABLE_NAMES = new Set<string>(ELEMENTS_APPEARANCE_VARIABLE_NAMES);
const RULE_SELECTORS = new Set<string>(ELEMENTS_APPEARANCE_RULE_SELECTORS);
const RULE_PROPERTIES = new Set<string>(ELEMENTS_APPEARANCE_RULE_PROPERTIES);
const UNSAFE_CSS_VALUE = /[{};]|<\/style|@import|expression\s*\(|javascript\s*:|url\s*\(/i;
const CSS_VARIABLE_REFERENCE = /var\(\s*--([A-Za-z][A-Za-z0-9]*)\s*\)/gi;
const ELEMENTS_APPEARANCE_VALUE_MAX_CHARACTERS = 512;

function hasOnlyPublicVariableReferences(value: string): boolean {
  const unresolved = value.replace(CSS_VARIABLE_REFERENCE, (_reference, name: string) =>
    VARIABLE_NAMES.has(name) ? '' : 'var(invalid)',
  );
  return !/var\s*\(/i.test(unresolved);
}

export function isSafeElementsAppearanceValue(value: unknown): value is string {
  return (
    typeof value === 'string' &&
    value.length > 0 &&
    value.length <= ELEMENTS_APPEARANCE_VALUE_MAX_CHARACTERS &&
    value === value.trim() &&
    !containsAsciiControlCharacter(value) &&
    !UNSAFE_CSS_VALUE.test(value) &&
    hasOnlyPublicVariableReferences(value)
  );
}

export function assertValidElementsAppearance(appearance: PaypercutElementsAppearance): void {
  if (!isRecord(appearance) || Object.keys(appearance).length === 0) {
    throw new Error('[Paypercut Elements] Appearance details are required.');
  }
  const allowedFields = new Set([
    'theme',
    'inputs',
    'labels',
    'variables',
    'rules',
    'disableAnimations',
  ]);
  if (Object.keys(appearance).some((field) => !allowedFields.has(field))) {
    throw new Error('[Paypercut Elements] Appearance contains an unsupported field.');
  }
  if (
    appearance.theme !== undefined &&
    appearance.theme !== 'light' &&
    appearance.theme !== 'dark'
  ) {
    throw new Error('[Paypercut Elements] Appearance theme must be light or dark.');
  }
  if (
    appearance.inputs !== undefined &&
    appearance.inputs !== 'condensed' &&
    appearance.inputs !== 'spaced'
  ) {
    throw new Error('[Paypercut Elements] Appearance inputs must be condensed or spaced.');
  }
  if (
    appearance.labels !== undefined &&
    appearance.labels !== 'auto' &&
    appearance.labels !== 'above' &&
    appearance.labels !== 'floating'
  ) {
    throw new Error('[Paypercut Elements] Appearance labels must be auto, above, or floating.');
  }
  if (
    appearance.disableAnimations !== undefined &&
    typeof appearance.disableAnimations !== 'boolean'
  ) {
    throw new Error('[Paypercut Elements] Appearance disableAnimations must be a boolean.');
  }
  if (appearance.variables !== undefined) {
    if (!isRecord(appearance.variables)) {
      throw new Error('[Paypercut Elements] Appearance variables are invalid.');
    }
    for (const [name, value] of Object.entries(appearance.variables)) {
      if (!VARIABLE_NAMES.has(name) || !isSafeElementsAppearanceValue(value)) {
        throw new Error(`[Paypercut Elements] Appearance variable ${name} is invalid.`);
      }
    }
  }
  if (appearance.rules !== undefined) {
    if (!isRecord(appearance.rules)) {
      throw new Error('[Paypercut Elements] Appearance rules are invalid.');
    }
    for (const [selector, declarations] of Object.entries(appearance.rules)) {
      if (!RULE_SELECTORS.has(selector) || !isRecord(declarations)) {
        throw new Error(`[Paypercut Elements] Appearance rule ${selector} is invalid.`);
      }
      for (const [property, value] of Object.entries(declarations)) {
        if (!RULE_PROPERTIES.has(property) || !isSafeElementsAppearanceValue(value)) {
          throw new Error(
            `[Paypercut Elements] Appearance rule ${selector}.${property} is invalid.`,
          );
        }
      }
    }
  }
}

export function cloneElementsAppearance(
  appearance: PaypercutElementsAppearance | undefined,
): PaypercutElementsAppearance | undefined {
  if (!appearance) return undefined;
  return {
    ...(appearance.theme ? { theme: appearance.theme } : {}),
    ...(appearance.inputs ? { inputs: appearance.inputs } : {}),
    ...(appearance.labels ? { labels: appearance.labels } : {}),
    ...(appearance.disableAnimations !== undefined
      ? { disableAnimations: appearance.disableAnimations }
      : {}),
    ...(appearance.variables ? { variables: { ...appearance.variables } } : {}),
    ...(appearance.rules
      ? {
          rules: Object.fromEntries(
            Object.entries(appearance.rules).map(([selector, declarations]) => [
              selector,
              { ...declarations },
            ]),
          ) as NonNullable<PaypercutElementsAppearance['rules']>,
        }
      : {}),
  };
}

export function elementsAppearanceEqual(
  left: PaypercutElementsAppearance | undefined,
  right: PaypercutElementsAppearance | undefined,
): boolean {
  return stableStringify(left ?? {}) === stableStringify(right ?? {});
}

function stableStringify(value: unknown): string {
  if (Array.isArray(value)) return `[${value.map(stableStringify).join(',')}]`;
  if (isRecord(value)) {
    return `{${Object.keys(value)
      .sort()
      .map((key) => `${JSON.stringify(key)}:${stableStringify(value[key])}`)
      .join(',')}}`;
  }
  return JSON.stringify(value);
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}
